import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addWeeks, startOfMonth, endOfMonth, isAfter } from 'date-fns';
import TeacherSidebar from './TeacherSidebar';
import TeacherTopSection from './TeacherTopSection';
import Calendar from './Calendar';
import Payout from './Payout';

export default function TeacherDashboard() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teacherDetails, setTeacherDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('home');
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editMode, setEditMode] = useState('individual');

  useEffect(() => {
    fetchData();
    setCurrentWeek(new Date());
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('No user found. Please log in.');
        return;
      }

      const { data: teacherData, error: teacherError } = await supabase
        .from('teachers')
        .select('*')
        .eq('id', user.id)
        .single();

      if (teacherError) throw teacherError;

      const { data: classesData, error: classesError } = await supabase
        .from('individual_classes')
        .select(`
          *,
          students:student_id (id, full_name, jitsi_link)
        `)
        .eq('teacher_id', user.id)
        .order('class_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (classesError) throw classesError;

      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('*')
        .eq('teacher_id', user.id);

      if (studentsError) throw studentsError;

      setTeacherDetails(teacherData);
      setClasses(classesData || []);
      setStudents(studentsData || []);
    } catch (error) {
      console.error('Error fetching data:', error.message);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  const nextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const prevWeek = () => setCurrentWeek(addWeeks(currentWeek, -1));

  const getFilteredClasses = (start, end) => {
    return eachDayOfInterval({ start, end }).map(day => ({
      date: day,
      classes: classes.filter(c => isSameDay(parseISO(c.class_date), day)),
    }));
  };

  const weekSchedule = getFilteredClasses(
    startOfWeek(currentWeek, { weekStartsOn: 1 }),
    endOfWeek(currentWeek, { weekStartsOn: 1 })
  );

  const monthSchedule = getFilteredClasses(
    startOfMonth(currentMonth),
    endOfMonth(currentMonth)
  );

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
  };

  const getFutureClasses = () => {
    const today = new Date();
    return classes.filter(c => isAfter(parseISO(c.class_date), today));
  };

  const handleEditSchedule = (student, mode) => {
    setSelectedStudent(student);
    setEditMode(mode);
    setIsEditModalOpen(true);
  };

  const handleSaveSchedule = async (updatedSchedule) => {
    try {
      setLoading(true);
      if (editMode === 'individual') {
        const { data, error } = await supabase
          .from('individual_classes')
          .update(updatedSchedule)
          .eq('id', updatedSchedule.id);

        if (error) throw error;
      } else {
        const today = new Date().toISOString().split('T')[0];
        const { error: deleteError } = await supabase
          .from('individual_classes')
          .delete()
          .eq('student_id', selectedStudent.id)
          .gte('class_date', today);

        if (deleteError) throw deleteError;

        const { error: insertError } = await supabase
          .from('individual_classes')
          .insert(updatedSchedule);

        if (insertError) throw insertError;
      }

      await fetchData();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating schedule:', error.message);
      setError('Failed to update schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <svg className="w-20 h-20 mx-auto mb-4" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle className="text-gray-600" stroke="currentColor" strokeWidth="4" fill="none" cx="50" cy="50" r="45" />
            <path className="text-blue-500" fill="currentColor" d="M50,25 L75,50 L50,75 L25,50 Z">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 50 50"
                to="360 50 50"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
          <h2 className="text-2xl font-semibold text-white mb-2">Teacher LMS</h2>
          <p className="text-gray-400">Loading your personalized learning experience...</p>
        </div>
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          svg circle {
            animation: dash 1.5s ease-in-out infinite;
          }
          @keyframes dash {
            0% {
              stroke-dasharray: 1, 150;
              stroke-dashoffset: 0;
            }
            50% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -35;
            }
            100% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -124;
            }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen bg-gray-100 text-red-600">{error}</div>;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <TeacherSidebar 
        teacherName={teacherDetails.full_name}
        onViewChange={setActiveView}
        activeView={activeView}
      />
      <div className="flex flex-col flex-grow overflow-hidden">
        <TeacherTopSection 
          teacherName={teacherDetails.full_name}
          onLogout={handleLogout}
        />
        <main className="flex-grow p-6 overflow-auto bg-gray-100">
          {activeView === 'home' && (
            <div className="space-y-6">
              <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-2xl font-semibold mb-4">Welcome, {teacherDetails.full_name}!</h2>
                <p className="text-gray-600 mb-4">Here's a summary of your teaching schedule:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Quick Stats</h3>
                    <p>Total Students: {students.length}</p>
                    <p>Total Classes (All Time): {classes.length}</p>
                    <p>Upcoming Classes: {getFutureClasses().length}</p>
                    <p>Classes This Week: {weekSchedule.reduce((sum, day) => sum + day.classes.length, 0)}</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Upcoming Classes</h3>
                    {getFutureClasses().slice(0, 3).map((classItem) => (
                      <div key={classItem.id} className="mb-2">
                        <p>{classItem.class_name} - {format(parseISO(classItem.class_date), 'MMM d, yyyy')}</p>
                        <p className="text-sm text-gray-600">
                          {format(parseISO(`2000-01-01T${classItem.start_time}`), 'h:mm a')} - 
                          {format(parseISO(`2000-01-01T${classItem.end_time}`), 'h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'profile' && (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">Teacher Profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p><strong>Name:</strong> {teacherDetails.full_name}</p>
                  <p><strong>Email:</strong> {teacherDetails.email}</p>
                  <p><strong>Phone:</strong> {teacherDetails.phone_number}</p>
                </div>
              </div>
            </div>
          )}

          {activeView === 'students' && (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">Your Students</h2>
              {students.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total/Remaining Classes</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Link</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {students.map((student) => {
                        const studentClasses = classes.filter(c => c.student_id === student.id);
                        const totalClasses = studentClasses.length;
                        const remainingClasses = studentClasses.filter(c => isAfter(parseISO(c.class_date), new Date())).length;
                        return (
                          <tr key={student.id}>
                            <td className="px-6 py-4 whitespace-nowrap">{student.full_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{student.grade}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{student.phone_number}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {totalClasses} / {remainingClasses}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <a href={student.jitsi_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">
                                Class Link
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button onClick={() => handleEditSchedule(student, 'individual')} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2">
                                Edit Class
                              </button>
                              <button onClick={() => handleEditSchedule(student, 'pattern')} className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
                                Change Pattern
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-600">No students assigned yet.</p>
              )}
            </div>
          )}

          {activeView === 'classes' && (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">Upcoming Classes</h2>
              {getFutureClasses().length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Link</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFutureClasses().map((classItem) => (
                        <tr key={classItem.id}>
                          <td className="px-6 py-4 whitespace-nowrap">{format(parseISO(classItem.class_date), 'MMM d, yyyy')}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {format(parseISO(`2000-01-01T${classItem.start_time}`), 'h:mm a')} - 
                            {format(parseISO(`2000-01-01T${classItem.end_time}`), 'h:mm a')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{classItem.class_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{classItem.students?.full_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <a href={classItem.students?.jitsi_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">
                              Class Link
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-600">No upcoming classes scheduled.</p>
              )}
            </div>
          )}

          {activeView === 'schedule' && (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">Weekly Schedule</h2>
              <p className="text-gray-600 mb-4">Classes for the week of {format(startOfWeek(currentWeek, { weekStartsOn: 1 }), 'MMMM d, yyyy')}</p>
              <div className="flex justify-between mb-4">
                <button onClick={prevWeek} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">&lt; Previous Week</button>
                <button onClick={nextWeek} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Next Week &gt;</button>
              </div>
              {weekSchedule.some(day => day.classes.length > 0) ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class Link</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {weekSchedule.flatMap((day) => 
                        day.classes.map((classItem, index) => (
                          <tr key={`${format(day.date, 'yyyy-MM-dd')}-${index}`}>
                            {index === 0 && (
                              <td rowSpan={day.classes.length} className="px-6 py-4 whitespace-nowrap">
                                {format(day.date, 'EEE, MMM d')}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">{classItem.class_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{classItem.students?.full_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {format(parseISO(`2000-01-01T${classItem.start_time}`), 'h:mm a')} - 
                              {format(parseISO(`2000-01-01T${classItem.end_time}`), 'h:mm a')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <a href={classItem.students?.jitsi_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">
                                Class Link
                              </a>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-600">No classes scheduled for this week.</p>
              )}
            </div>
          )}

          {activeView === 'payout' && (
            <Payout teacherId={teacherDetails.id} />
          )}

          {activeView === 'help' && (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4">Help and Support</h2>
              <p className="mb-4">If you need assistance or have any questions, please don't hesitate to reach out to our support team.</p>
              <p className="mb-4">Email: support@akademix.com</p>
              <p className="mb-4">Phone: +91 9311363693</p>
              <p>Our support hours are Monday to Friday, 9:00 AM to 5:00 PM EST.</p>
            </div>
          )}
        </main>
      </div>

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">
              {editMode === 'individual' ? 'Edit Class' : 'Change Schedule Pattern'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {editMode === 'individual' 
                ? 'Update the details for this specific class.' 
                : 'Change the overall schedule pattern for this student.'}
            </p>
            {editMode === 'individual' ? (
              <IndividualClassEdit 
                student={selectedStudent} 
                onSave={handleSaveSchedule}
                classes={classes.filter(c => c.student_id === selectedStudent?.id)}
              />
            ) : (
              <SchedulePatternEdit 
                student={selectedStudent} 
                onSave={handleSaveSchedule}
              />
            )}
            <button 
              onClick={() => setIsEditModalOpen(false)}
              className="mt-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function IndividualClassEdit({ student, onSave, classes }) {
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDate, setClassDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  useEffect(() => {
    if (selectedClass) {
      setClassDate(selectedClass.class_date);
      setStartTime(selectedClass.start_time);
      setEndTime(selectedClass.end_time);
    }
  }, [selectedClass]);

  const handleSave = () => {
    if (selectedClass) {
      onSave({
        id: selectedClass.id,
        class_date: classDate,
        start_time: startTime,
        end_time: endTime,
      });
    }
  };

  return (
    <div className="space-y-4">
      <select 
        onChange={(e) => setSelectedClass(classes.find(c => c.id === e.target.value))}
        className="w-full p-2 border border-gray-300 rounded-md"
      >
        <option value="">Select a class</option>
        {classes.map((classItem) => (
          <option key={classItem.id} value={classItem.id}>
            {format(parseISO(classItem.class_date), 'MMM d, yyyy')} - {classItem.class_name}
          </option>
        ))}
      </select>
      {selectedClass && (
        <>
          <div>
            <label htmlFor="class-date" className="block text-sm font-medium text-gray-700">Class Date</label>
            <input 
              id="class-date" 
              type="date" 
              value={classDate} 
              onChange={(e) => setClassDate(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
            />
          </div>
          <div>
            <label htmlFor="start-time" className="block text-sm font-medium text-gray-700">Start Time</label>
            <input 
              id="start-time" 
              type="time" 
              value={startTime} 
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
            />
          </div>
          <div>
            <label htmlFor="end-time" className="block text-sm font-medium text-gray-700">End Time</label>
            <input 
              id="end-time" 
              type="time" 
              value={endTime} 
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
            />
          </div>
        </>
      )}
      <button 
        onClick={handleSave} 
        disabled={!selectedClass}
        className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
      >
        Save Changes
      </button>
    </div>
  );
}

function SchedulePatternEdit({ student, onSave }) {
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [numberOfClasses, setNumberOfClasses] = useState('');

  const handleSave = () => {
    const newSchedule = [];
    let currentDate = new Date(startDate);
    for (let i = 0; i < parseInt(numberOfClasses); i++) {
      while (!daysOfWeek.includes(currentDate.getDay())) {
        currentDate.setDate(currentDate.getDate() + 1);
      }
      newSchedule.push({
        student_id: student.id,
        class_date: format(currentDate, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: endTime,
        class_name: `Class ${i + 1}`,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    onSave(newSchedule);
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="days-of-week" className="block text-sm font-medium text-gray-700">Days of Week</label>
        <select 
          id="days-of-week"
          multiple
          value={daysOfWeek}
          onChange={(e) => setDaysOfWeek(Array.from(e.target.selectedOptions, option => parseInt(option.value)))}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
        >
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
            <option key={day} value={index}>{day}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="start-time" className="block text-sm font-medium text-gray-700">Start Time</label>
        <input 
          id="start-time" 
          type="time" 
          value={startTime} 
          onChange={(e) => setStartTime(e.target.value)}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
        />
      </div>
      <div>
        <label htmlFor="end-time" className="block text-sm font-medium text-gray-700">End Time</label>
        <input 
          id="end-time" 
          type="time" 
          value={endTime} 
          onChange={(e) => setEndTime(e.target.value)}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
        />
      </div>
      <div>
        <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
        <input 
          id="start-date" 
          type="date" 
          value={startDate} 
          onChange={(e) => setStartDate(e.target.value)}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
        />
      </div>
      <div>
        <label htmlFor="number-of-classes" className="block text-sm font-medium text-gray-700">Number of Classes</label>
        <input 
          id="number-of-classes" 
          type="number" 
          value={numberOfClasses} 
          onChange={(e) => setNumberOfClasses(e.target.value)}
          className="mt-1 block w-full p-2 border border-gray-300 rounded-md" 
        />
      </div>
      <button 
        onClick={handleSave}
        className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
      >
        Save Changes
      </button>
    </div>
  );
}